import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { calculateBasicPrice, calculateMetricFactor } from 'app/ui/utils/prices';

@Component({
  selector: 'np-price-preview',
  templateUrl: './pricePreview.component.html',
  styleUrl: './pricePreview.component.css',
  standalone: false
})
export class PricePreviewComponent {
  @Input() price: number | null = null;
  @Input() currency: string;
  @Input() isNetPrice = true;
  @Input() priceQuantity: number | string;
  @Input() orderUnit: string;
  @Input() basicQuantity: number;
  @Input() contentQuantity: number;
  @Input() contentPackage: string;
  @Input() contentUnit: string;
  @Input() showStandardPrice = false;

  constructor(private translate: TranslateService) {}

  get priceQuantityNumber(): number | null {
    if (this.priceQuantity == null || this.priceQuantity === '') return null;
    const quantity = Number(this.priceQuantity.toString().replace(',', '.'));
    return quantity && !isNaN(quantity) ? quantity : null;
  }

  get standardPricePreview(): string | null {
    if (!this.showStandardPrice || this.price == null) return null;
    return this.formatPreview(this.formatPrice(this.price), this.priceQuantityNumber || 1, this.orderUnitText);
  }

  get basicPricePreview(): string | null {
    if (!this.basicQuantity) return null;
    const contentPackage = this.contentPackage
      ? this.translate.instant(this.contentPackage)
      : `<${this.translate.instant('Inhaltseinheit')}>`;
    return this.formatPreview(this.basicPriceText, this.basicQuantity, contentPackage);
  }

  get pricePerOrderUnitPreview(): string | null {
    const priceQuantity = this.priceQuantityNumber;
    if (!priceQuantity || priceQuantity === 1 || this.price == null) return null;
    return this.formatPreview(this.formatPrice(this.price / priceQuantity), 1, this.orderUnitText);
  }

  private get basicPriceText(): string {
    if (this.price == null) return `<${this.translate.instant('Preis')}>`;
    const metricFactor = calculateMetricFactor(this.contentUnit, this.contentPackage);
    const basicPrice = calculateBasicPrice(
      this.price,
      this.priceQuantityNumber,
      this.basicQuantity,
      this.contentQuantity,
      metricFactor
    );
    return this.formatPrice(basicPrice);
  }

  private get orderUnitText(): string {
    return this.orderUnit
      ? this.translate.instant(this.orderUnit)
      : `<${this.translate.instant('Bestelleinheit')}>`;
  }

  private formatPreview(price: string, quantity: number, unit: string): string {
    const key = this.isNetPrice ? 'netto je Menge / Einheit' : 'Preis je Menge / Einheit';
    return this.translate.instant(key, {
      basicNetPrice: price,
      price,
      priceQuantity: quantity.toLocaleString(this.translate.currentLang),
      orderUnit: unit
    });
  }

  private formatPrice(price: number): string {
    if (!this.currency || !this.currency.trim()) {
      return price.toLocaleString(this.translate.currentLang);
    }
    return price.toLocaleString(this.translate.currentLang, {
      style: 'currency',
      currency: this.currency
    });
  }
}
